import React, { useState, useEffect } from 'react'
import { BarChart3, Download, Upload, Settings } from 'lucide-react'
import { SimulationAPI } from '../lib/api'
import type { SimulationStatus, Metrics } from '../lib/types'
import { useLocale } from '../i18n/LocaleContext'

interface ControlsProps {
  simulationId: string | null
  status: SimulationStatus | null
  onStatusUpdate: (id: string) => void
  availableSpecies?: string[]
  selectedSubstance?: string | null
  onSubstanceChange?: (id: string | null) => void
  runtimeMs?: number
}

const Controls: React.FC<ControlsProps> = ({
  simulationId,
  status,
  onStatusUpdate,
  availableSpecies,
  selectedSubstance,
  onSubstanceChange,
  runtimeMs
}) => {
  const { t } = useLocale()
  const [metrics, setMetrics] = useState<Metrics | null>(null)
  const [novelSubstances, setNovelSubstances] = useState<any[]>([])
  const [showSettings, setShowSettings] = useState(false)
  const [snapshotFilename, setSnapshotFilename] = useState('')
  // species selection handled via props

  const api = new SimulationAPI()

  useEffect(() => {
    if (simulationId) {
      updateMetrics()
      updateNovelSubstances()
      // Poll concentrations species list if present via metrics hint soon
      
      // Update metrics every 5 seconds
      const interval = setInterval(() => {
        updateMetrics()
        updateNovelSubstances()
      }, 5000)

      return () => clearInterval(interval)
    }
  }, [simulationId])

  const updateMetrics = async () => {
    if (!simulationId) return
    
    try {
      const response = await api.getMetrics(simulationId)
      setMetrics(response.metrics)
    } catch (error: any) {
      // Stop polling if simulation doesn't exist
      if (error?.message?.includes('404') || error?.message?.includes('not found')) {
        console.warn('Simulation not found, stopping metrics updates')
        return
      }
      console.error('Failed to update metrics:', error)
    }
  }

  const updateNovelSubstances = async () => {
    if (!simulationId) return
    
    try {
      const response = await api.getNovelSubstances(simulationId, 5)
      // BUGFIX: Deduplicate substances by ID to prevent React key warnings
      const uniqueSubstances = response.substances.filter((substance: any, index: number, self: any[]) => 
        index === self.findIndex((s: any) => s.id === substance.id)
      )
      setNovelSubstances(uniqueSubstances)
    } catch (error: any) {
      // Stop polling if simulation doesn't exist
      if (error?.message?.includes('404') || error?.message?.includes('not found')) {
        console.warn('Simulation not found, stopping novel substances updates')
        return
      }
      console.error('Failed to update novel substances:', error)
    }
  }

  const handleSaveSnapshot = async () => {
    if (!simulationId) return
    
    try {
      const filename = snapshotFilename || `snapshot_${Date.now()}.json`
      await api.saveSnapshot(simulationId, filename)
      alert(t('controls.alert.saveOk'))
      setSnapshotFilename('')
    } catch (error) {
      console.error('Failed to save snapshot:', error)
      alert(t('controls.alert.saveFail'))
    }
  }

  const handleLoadSnapshot = async () => {
    if (!simulationId || !snapshotFilename) return
    
    try {
      await api.loadSnapshot(simulationId, snapshotFilename)
      alert(t('controls.alert.loadOk'))
      onStatusUpdate(simulationId)
    } catch (error) {
      console.error('Failed to load snapshot:', error)
      alert(t('controls.alert.loadFail'))
    }
  }

  const formatNumber = (num: number): string => {
    if (num >= 1000000) {
      return (num / 1000000).toFixed(1) + 'M'
    } else if (num >= 1000) {
      return (num / 1000).toFixed(1) + 'K'
    }
    return num.toFixed(1)
  }

  const formatTime = (seconds: number): string => {
    if (!Number.isFinite(seconds)) return '0:00.00'
    if (seconds < 60) {
      const mins = Math.floor(seconds / 60)
      const secs = Math.floor(seconds % 60)
      const ms = Math.floor((seconds - Math.floor(seconds)) * 100)
      return `${mins}:${secs.toString().padStart(2, '0')}.${ms.toString().padStart(2, '0')}`
    }
    const hours = Math.floor(seconds / 3600)
    const minutes = Math.floor((seconds % 3600) / 60)
    const secs = Math.floor(seconds % 60)
    return `${hours}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`
  }

  return (
    <div className="controls">
      <div className="control-group">
        <h2 className="text-lg font-semibold text-white mb-4">{t('controls.title')}</h2>
        
        {/* Status */}
        <div className="control-group">
          <div className="text-sm font-semibold text-gray-300 mb-1">{t('controls.status')}</div>
          <div className="flex items-center gap-2">
            <div className={`w-2 h-2 rounded-full ${
              status?.is_running ? (status.is_paused ? 'bg-yellow-500' : 'bg-green-500') : 'bg-red-500'
            }`} />
            <span className="text-sm">
              {status?.is_running ? (status.is_paused ? t('app.status.paused') : t('app.status.running')) : t('app.status.stopped')}
            </span>
          </div>
        </div>

        {/* Time (wall-clock runtime) */}
        {typeof runtimeMs === 'number' && (
          <div className="control-group">
            <div className="text-sm font-semibold text-gray-300 mb-1">{t('controls.wallClock')}</div>
            <div className="text-sm font-mono">
              {formatTime((runtimeMs as number) / 1000)}
            </div>
          </div>
        )}

        {/* Simulation Time (from backend) */}
        {status && (
          <div className="control-group">
            <div className="text-sm font-semibold text-gray-300 mb-1">{t('controls.simTime')}</div>
            <div className="text-sm font-mono">
              {status.current_time.toFixed(2)}s
            </div>
          </div>
        )}

        {/* Step Count */}
        {status && (
          <div className="control-group">
            <div className="text-sm font-semibold text-gray-300 mb-1">{t('controls.stepCount')}</div>
            <div className="text-sm font-mono">
              {formatNumber(status.step_count)}
            </div>
          </div>
        )}
      </div>

      {/* Preset Mode: Species Selection (if concentrations present via frontend main view) */}
      {Array.isArray(availableSpecies) && (availableSpecies as string[]).length > 0 && (
        <div className="control-group">
          <label htmlFor="species-select">{t('controls.species.label')}</label>
          <select
            id="species-select"
            className="w-full text-sm"
            value={selectedSubstance || ''}
            onChange={(e) => onSubstanceChange?.(e.target.value || null)}
            aria-label={t('controls.species.aria')}
          >
            <option value="">{t('common.all')}</option>
            {(availableSpecies as string[]).map((s: string) => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>
        </div>
      )}

      {/* Metrics */}
      {metrics && (
        <div className="control-group">
          <h3 className="text-md font-semibold text-white mb-2 flex items-center gap-2">
            <BarChart3 size={16} />
            {t('controls.metrics')}
          </h3>
          <div className="metrics">
            <div className="metric">
              <div className="metric-label">{t('common.particles')}</div>
              <div className="metric-value">{formatNumber(metrics.particle_count ?? 0)}</div>
            </div>
            <div className="metric">
              <div className="metric-label">{t('common.bonds')}</div>
              <div className="metric-value">{formatNumber(metrics.bond_count ?? 0)}</div>
            </div>
            <div className="metric">
              <div className="metric-label">{t('common.clusters')}</div>
              <div className="metric-value">{formatNumber(metrics.cluster_count ?? 0)}</div>
            </div>
            <div className="metric">
              <div className="metric-label">{t('controls.metric.noveltyRate')}</div>
              <div className="metric-value">{(((metrics.novelty_rate ?? 0) * 100)).toFixed(1)}%</div>
            </div>
            <div className="metric">
              <div className="metric-label">{t('controls.metric.healthScore')}</div>
              <div className="metric-value">{
                Number.isFinite(metrics.health_score ?? 0)
                  ? (((metrics.health_score ?? 0) * 100).toFixed(1) + '%')
                  : '0.0%'
              }</div>
            </div>
            <div className="metric">
              <div className="metric-label">{t('controls.metric.totalEnergy')}</div>
              <div className="metric-value">{formatNumber(metrics.total_energy ?? 0)}</div>
            </div>
          </div>
        </div>
      )}

      {/* Novel Substances */}
      {novelSubstances.length > 0 && (
        <div className="control-group">
          <h3 className="text-md font-semibold text-white mb-2">{t('controls.discoveries')}</h3>
          <div className="substance-list">
            {novelSubstances.slice(0, 3).map((substance, index) => (
              <div key={`${substance.id}_${index}`} className="substance-item">
                <div className="substance-id">{substance.id.slice(-8)}</div>
                <div className="substance-props">
                  {t('controls.discovery.props', {
                    size: substance.size,
                    complexity: (substance.complexity || 0).toFixed(1),
                  })}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Snapshot Controls */}
      <div className="control-group">
        <h3 className="text-md font-semibold text-white mb-2 flex items-center gap-2">
          <Settings size={16} />
          {t('controls.snapshots')}
        </h3>
        
        <div className="flex flex-col gap-2">
          <label htmlFor="snapshot-filename" className="text-sm text-gray-300">{t('controls.snapshot.filename')}</label>
          <input
            id="snapshot-filename"
            name="snapshot-filename"
            type="text"
            placeholder={t('controls.snapshot.placeholder')}
            value={snapshotFilename}
            onChange={(e) => setSnapshotFilename(e.target.value)}
            className="text-sm"
          />
          
          <div className="flex gap-2">
            <button
              className="btn btn-secondary flex-1"
              onClick={handleSaveSnapshot}
              disabled={!simulationId}
            >
              <Download size={14} />
              {t('common.save')}
            </button>
            
            <button
              className="btn btn-secondary flex-1"
              onClick={handleLoadSnapshot}
              disabled={!simulationId || !snapshotFilename}
            >
              <Upload size={14} />
              {t('common.load')}
            </button>
          </div>
        </div>
      </div>

      {/* Settings Toggle */}
      <div className="control-group">
        <button
          className="btn btn-secondary w-full"
          onClick={() => setShowSettings(!showSettings)}
        >
          <Settings size={16} />
          {showSettings ? t('controls.settings.hide') : t('controls.settings.show')}
        </button>
      </div>

      {/* Advanced Settings */}
      {showSettings && (
        <div className="control-group">
          <h3 className="text-md font-semibold text-white mb-2">{t('controls.settings.advanced')}</h3>
          
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <label htmlFor="auto-save-checkbox" className="text-sm">{t('controls.settings.autoSave')}</label>
              <input id="auto-save-checkbox" name="auto-save-checkbox" type="checkbox" className="w-4 h-4" />
            </div>
            
            <div className="flex items-center justify-between">
              <label htmlFor="show-ids-checkbox" className="text-sm">{t('controls.settings.showIds')}</label>
              <input id="show-ids-checkbox" name="show-ids-checkbox" type="checkbox" className="w-4 h-4" />
            </div>
            
            <div className="flex items-center justify-between">
              <label htmlFor="high-contrast-checkbox" className="text-sm">{t('controls.settings.highContrast')}</label>
              <input id="high-contrast-checkbox" name="high-contrast-checkbox" type="checkbox" className="w-4 h-4" />
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default Controls
